/* @flow */

import React, { PureComponent } from 'react'
import Basic, { InputContext } from './basic'

import type { BasicPropsType, BasicStateType } from './basic'

type RadioOptionPropsType = BasicPropsType & {
  id: string,
  name: string,
  value: string,
  checked: boolean,
  disabled?: boolean,
  onChange: (val: string) => void,
  optionInputClassName: string,
  optionLabelClassName: string,
  optionSpanClassName: string,
  optionInfoClassName: string,
  labelClassName?: string,
  directionClassName?: string
}

class RadioOption extends PureComponent<RadioOptionPropsType, BasicStateType> {
  props: RadioOptionPropsType

  getValue = (event: SyntheticInputEvent<any>) => {
    const { onChange } = this.props
    if (typeof onChange == 'function') onChange(event.target.value)
  }

  id(): string {
    const { id, name, value } = this.props
    return `${id || name || ''}-${String(value) || ''}`
  }

  formField() {
    const { name, value, checked, disabled, optionInputClassName, tabIndex } = this.props
    return (
      <input
        id={this.id()}
        type="radio"
        className={optionInputClassName}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={this.getValue}
        tabIndex={tabIndex}
      />
    )
  }

  render(): React$Element<*> {
    const { label, info, optionLabelClassName, optionSpanClassName, optionInfoClassName, labelClassName } = this.props
    return (
      <label className={`${optionLabelClassName} ${labelClassName || ''}`}>
        {this.formField()}{' '}
        <span id={`${this.id()}-label`} className={optionSpanClassName}>
          {label}
        </span>
        {info && (
          <span id={`${this.id()}-info`} className={optionInfoClassName}>
            {info}
          </span>
        )}
      </label>
    )
  }
}

export type ValuesEntryType = {
  value: string | number | boolean,
  label: string,
  info?: string,
  disabled?: boolean,
  tabIndex?: number,
  OptionComponent?: PureComponent<*, *>
}
export type ValuesType = Array<ValuesEntryType>
export type RadioPropsType = BasicPropsType & {
  value?: string | number | boolean,
  values: ValuesType,
  optionInputClassName?: string,
  optionLabelClassName?: string,
  optionSpanClassName?: string,
  optionInfoClassName?: string,
  optionGroupClassName?: string,
  directionVerticalClassName?: string,
  directionHorizontalClassName?: string,
  OptionComponent?: PureComponent<RadioOptionPropsType, {}>,
  direction?: string
}

export type RadioStateType = BasicStateType & {}

class Radio extends Basic<RadioPropsType, RadioStateType> {
  props: RadioPropsType
  handleChange: any

  static defaultProps = {
    ...Basic.defaultProps,
    optionInputClassName: 'atp-input__radio',
    optionLabelClassName: 'atp-input__label',
    optionSpanClassName: 'atp-input__label__text',
    optionInfoClassName: 'atp-input__label__info',
    optionGroupClassName: 'atp-input__radios',
    directionVerticalClassName: 'atp-input--vertical',
    directionHorizontalClassName: 'atp-input--horizontal',
    OptionComponent: RadioOption,
    direction: 'vertical'
  }

  static contextType = InputContext

  getProps() {
    return {
      ...this.props,
      ...this.context
    }
  }

  handleChange = (value: string | number | boolean): void => {
    const { onChange } = this.props
    if (['true', 'false'].includes(value)) {
      value = value === 'true' ? true : false
    }
    if (onChange) onChange(value)
  }

  get value(): ?string {
    let value = this.props.value
    if (typeof value === 'boolean') {
      value = value ? 'true' : 'false'
    } else if (value == null) {
      return null
    }
    return `${value}`
  }

  formField(): ?React$Element<*> {
    const props = this.getProps()
    const {
      values,
      name,
      OptionComponent,
      optionInputClassName,
      optionInfoClassName,
      optionLabelClassName,
      optionSpanClassName,
      optionGroupClassName,
      directionVerticalClassName,
      directionHorizontalClassName,
      direction
    } = props
    return (
      <div
        className={[
          optionGroupClassName,
          direction == 'vertical' ? directionVerticalClassName : directionHorizontalClassName
        ].join(' ')}>
        {values.map((entry: ValuesEntryType, index) => {
          const EntryOptionComponent = entry.OptionComponent || OptionComponent
          return (
            // $FlowFixMe empty error
            <EntryOptionComponent
              key={index}
              id={this.id}
              index={index}
              values={values}
              name={name}
              checked={entry.value === this.value}
              onChange={this.handleChange}
              optionInputClassName={optionInputClassName}
              optionLabelClassName={optionLabelClassName}
              optionSpanClassName={optionSpanClassName}
              optionInfoClassName={optionInfoClassName}
              {...entry}
              disabled={entry.disabled || props.disabled}
              tabIndex={entry.tabIndex || props.tabIndex}
            />
          )
        })}
      </div>
    )
  }
}

export default Radio
export { RadioOption }
